import React, { useState } from 'react';
import { Button, Modal, Typography, message } from 'antd';
import { ClockCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import ContentModal from './ContentModal';
import ReviewFormModal from './ReviewFormModal';
import ReviewIssuesModal from './ReviewIssuesModal';
import ReviewsTable from './ReviewsTable';
import { authorName, isReviewAuthor } from './authors';
import { useContentViewer } from './useContentViewer';
import {
	useDeleteProgramReviewMutation,
	useGetProgramReviewsQuery,
} from '../../redux/api/manufacturing';
import { useGetLoggedInUserQuery } from '../../redux/api/users';
import type { ProgramReview } from '../../interfaces/CamProgramming.interface';

const { Text } = Typography;

interface ProgramReviewsModalProps {
	programId: string | null;
	partName: string;
	open: boolean;
	onClose: () => void;
}

/**
 * All reviews for one program in the same table used on the order line item
 * scoring section. Opened from the program scoring grid when more than two
 * reviews exist.
 */
const ProgramReviewsModal: React.FC<ProgramReviewsModalProps> = ({
	programId,
	partName,
	open,
	onClose,
}) => {
	const { data, isLoading } = useGetProgramReviewsQuery(
		{ program: programId as string },
		{ skip: !programId || !open }
	);
	const { data: currentUser } = useGetLoggedInUserQuery();
	const [deleteReview] = useDeleteProgramReviewMutation();

	const [editOpen, setEditOpen] = useState(false);
	const [editingReview, setEditingReview] = useState<ProgramReview | null>(null);
	const viewer = useContentViewer<ProgramReview>();
	const issuesViewer = useContentViewer<ProgramReview>();

	const reviews = data?.results ?? [];

	const isAuthor = (review: ProgramReview) =>
		isReviewAuthor(review, currentUser?.id);

	const handleOpenEdit = (review: ProgramReview) => {
		setEditingReview(review);
		setEditOpen(true);
	};

	const handleDelete = async (review: ProgramReview) => {
		if (!programId) {
			return;
		}
		try {
			await deleteReview({ id: review.id, programId }).unwrap();
			message.success('Review removed');
		} catch {
			message.error('Failed to remove review');
		}
	};

	return (
		<>
			<Modal
				className="cam-modal"
				title={`Reviews — ${partName}`}
				open={open}
				centered
				onCancel={onClose}
				footer={<Button onClick={onClose}>Close</Button>}
				width={920}
				destroyOnClose
			>
				<ReviewsTable
					reviews={reviews}
					loading={isLoading}
					canEdit={isAuthor}
					onView={viewer.view}
					onViewIssues={issuesViewer.view}
					onEdit={handleOpenEdit}
					onDelete={handleDelete}
				/>
			</Modal>

			{programId && (
				<ReviewFormModal
					programId={programId}
					review={editingReview}
					open={editOpen}
					allowReviewerOverride={false}
					onClose={() => setEditOpen(false)}
				/>
			)}

			<ContentModal
				title={`Review by ${authorName(
					viewer.entry?.reviewer ?? null,
					'Unassigned'
				)}`}
				meta={
					viewer.entry && (
						<>
							{viewer.entry.review_duration_minutes != null && (
								<Text type="secondary" className="cam-entry__chip">
									<ClockCircleOutlined />{' '}
									{viewer.entry.review_duration_minutes} min
								</Text>
							)}
							{viewer.entry.review_completed_at && (
								<Text type="secondary">
									Completed{' '}
									{dayjs(viewer.entry.review_completed_at).format(
										'MMM D, YYYY [at] h:mm A'
									)}
								</Text>
							)}
						</>
					)
				}
				html={viewer.entry?.feedback ?? null}
				emptyText="No feedback"
				open={viewer.open}
				onClose={viewer.close}
				afterClose={viewer.afterClose}
			/>

			<ReviewIssuesModal
				programId={programId ?? ''}
				review={issuesViewer.entry}
				open={issuesViewer.open}
				onClose={issuesViewer.close}
				afterClose={issuesViewer.afterClose}
			/>
		</>
	);
};

export default ProgramReviewsModal;
